<template>
  <InputGroup
    :readonly="readonly"
    :bs="{
      width: 120,
      ...styles.input,
      ...bs,
    }"
    v-bind="inputAttrs"
    @change="v => handleChange(v.target.value, v.target)"
  >
    <template #left>
      <Icon
        v-if="!readonly"
        :bs="{
          ...styles.icon,
          borderRight: styles.border,
        }"
        :size="14"
        :grid-size="14"
        aria-label="minus"
        color="mute"
        icon="M1 7a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11A.5.5 0 011 7z"
        @click="() => handleChange(modelValue - 1)"
      />
    </template>
    <template #right>
      <Icon
        v-if="!readonly"
        :bs="{
          ...styles.icon,
          borderLeft: styles.border,
        }"
        color="mute"
        aria-label="plus"
        icon="add"
        @click="() => handleChange(modelValue + 1)"
      />
    </template>
  </InputGroup>
</template>

<script lang="ts">
  import {
    defineComponent, PropType, computed,
  } from 'vue'
  import { isNaN, isNil } from 'lodash'
  import Icon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import InputGroup from '../InputGroup/InputGroup.vue'
  import { BeerStyleObj } from '../../../core'

  /**
   * 小数字输入框
   * @see 继承InputGroup #InputGroup
   */
  export default defineComponent({
    name: 'CountInput',
    components: {
      Icon,
      InputGroup,
    },
    props: {
      /**
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * @ignore
       * 是否只读显示
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 加减符号点击后的增减的数量
       */
      step: {
        type: Number,
        default: 1,
      },
      /**
       * 最小数值
       */
      min: {
        type: Number,
        default: 0,
      },
      /**
       * 最大数值
       */
      max: {
        type: Number,
        default: 100,
      },
      /**
       * 是否必填，必填则会fallback 到 min
       */
      required: {
        type: Boolean,
        default: true,
      },
      /**
       * 控制的值
       */
      modelValue: {
        type: Number,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { attrs, emit }) {
      const styles = {
        input: {
          input: {
            textAlign: 'center',
            px: 40,
          },
        },
        icon: {
          height: 30,
          px: 10,
          cursor: 'pointer',
          '&:hover': {
            fill: 'brand',
          },
          userSelect: 'none',
        },
        border: '1px solid #d9d9d9',
      }
      const inputAttrs = computed(() => {
        const attr: any = {
          step: props.step,
          min: props.min,
          max: props.max,
          type: 'number',
          readonly: props.readonly,
          ...attrs,
        }
        if (props.disabled) {
          attr.disabled = true
        }
        if (!isNil(props.modelValue)) {
          attr.value = props.modelValue
        }
        return attr
      })

      function resetElement(
        value: string|number,
        el?: HTMLInputElement,
      ) {
        if (el) {
          // eslint-disable-next-line no-param-reassign
          el.value = String(value)
        }
      }
      const handleChange = (v: string, e?: HTMLInputElement) => {
        if (props.disabled) {
          return
        }
        if (v === '' && !props.required) {
          emit('update:modelValue', undefined)
          return
        }
        let num = Number(v)
        if (isNaN(num)) {
          num = props.min
          resetElement(num, e)
        }
        if (num < props.min) {
          num = props.min
          resetElement(num, e)
        }
        if (num > props.max) {
          num = props.max
          resetElement(num, e)
        }
        emit('update:modelValue', num)
      }

      return {
        styles,
        inputAttrs,
        handleChange,
      }
    },
  })
</script>
